import { FunctionComponent } from 'react'
import { hot } from 'react-hot-loader/root'
import { setConfig } from 'react-hot-loader'
import { Route, Switch } from 'react-router-dom'
import loadable from '@loadable/component'

// Components
import ThemeHelper from 'components/base/themehelper'
import Draft from 'components/base/draft'
import Footer from 'components/footer'
import Header from 'components/header'
import PayPage from 'pages/pay'
import UserSong from 'pages/usersong'
import UserSongs from 'pages/usersongs'
import SidebarWithMenuLayout from 'components/sidebar/layout'
import FixedFooter from 'components/fixedfooter'

// Utils
import { UserIsNotAuthenticatedRedir, UserIsAuthenticatedOrShowLogin, UserIsAuthenticatedAndStaffRedir } from 'authenticated'
import { safeLocalStorage as localStorage } from 'utils'

import styles from './global.css'

// Loadables
const ArtistPage = loadable(() => import('pages/artist'))
const ArtistsPage = loadable(() => import('pages/artists'))
const BlogListPage = loadable(() => import('pages/bloglist'))
const BlogPage = loadable(() => import('pages/blog'))
const CampaignPage = loadable(() => import('pages/campaign'))
const CollectionPage = loadable(() => import('pages/collection'))
const CollectionsPage = loadable(() => import('pages/collections'))

const DiscoverPage = loadable(() => import('pages/discover'))
const ErrorPage = loadable(() => import('pages/error'))
const FavoritesPage = loadable(() => import('pages/favorites'))
const FrontPage = loadable(() => import('pages/frontpage'))
const GenrePage = loadable(() => import('pages/genre'))
const GenresPage = loadable(() => import('pages/genres'))
const InterestForm = loadable(() => import('pages/interestform'))
const LabelPage = loadable(() => import('pages/label'))
const LabelsPage = loadable(() => import('pages/labels'))
const Notifications = loadable(
  () =>
    import(
      /* webpackPrefetch: true */
      'components/base/notifications'
    ),
)

const ProfilePage = loadable(() => import('pages/profile'))
const PurchasedTracksPage = loadable(() => import('pages/purchasedtracks'))
const RevenueReportingPage = loadable(() => import('pages/revenuereporting'))
const SettingsPage = loadable(() => import('pages/settings'))
const SongPage = loadable(() => import('pages/song'))
const StaticPage = loadable(() => import('pages/staticpages'))
const SubscribePage = loadable(() => import('pages/subscribe'))
const TracksPage = loadable(() => import('pages/browse'))
const User = loadable(() => import('pages/user'))
const Onboard = loadable(() => import('pages/onboard'))
const Beat = loadable(() => import('pages/beat'))

const sidebarRoutes = [
  {
    path: '/inspiration/:tab?',
    component: DiscoverPage,
  },
  {
    path: '/tracks/',
    component: TracksPage,
    exact: true,
  },
  {
    path: '/song/:slug',
    component: SongPage,
  },
  {
    path: '/genres/',
    component: GenresPage,
  },
  {
    path: '/genre/:slug',
    component: GenrePage,
  },
  {
    path: '/collections/',
    component: CollectionsPage,
  },
  {
    path: '/collection/:id',
    component: CollectionPage,
  },
  {
    path: '/artists/',
    component: ArtistsPage,
  },
  {
    path: '/artist/:slug',
    component: ArtistPage,
  },
  {
    path: '/labels/',
    component: LabelsPage,
  },
  {
    path: '/label/:slug',
    component: LabelPage,
  },
  {
    path: '/purchasedtracks/',
    component: UserIsAuthenticatedOrShowLogin(PurchasedTracksPage as FunctionComponent),
  },
  {
    path: '/favorites/',
    component: UserIsAuthenticatedOrShowLogin(FavoritesPage as FunctionComponent),
  },
]

const SidebarRoutes = (): JSX.Element => (
  <>
    {sidebarRoutes.map(({ path, exact = false, component }) => (
      <Route key={path} path={path} exact={exact} component={component} />
    ))}
  </>
)

const staticRoutes = [
  { path: '/page/:pageId', wrapperFunc: UserIsAuthenticatedAndStaffRedir },
  { path: '/about' },
  { path: '/press' },
  { path: '/terms' },
  { path: '/legal' },
  { path: '/privacy' },
  { path: '/cookies' },
  { path: '/contact' },
  { path: '/howitworks' },
  { path: '/plans' },
  { path: '/howtouse' },
  { path: '/music' },
  { path: '/licensing' },
  { path: '/support' },
  { path: '/privacy' },
  { path: '/roulette' },
  { path: '/rightsholders' },
  { path: '/blog', exact: true },
  { path: '/privacy' },
  { path: '/testimonials' },
  { path: '/referrals', wrapperFunc: UserIsAuthenticatedOrShowLogin },
]

setConfig({
  reloadHooks: false,
})

const App = () => {
  return (
    <>
      <ThemeHelper />
      <Draft />
      <a className={styles.skipToContent} href="#main">
        Skip navigation
      </a>
      <Header />
      <main id="main">
        <Switch>
          <Route path="/" exact component={UserIsNotAuthenticatedRedir(FrontPage as FunctionComponent)} />
          <Route exact path="/subscribe/:code?" component={SubscribePage} />
          <Route exact path="/campaign/:name" component={CampaignPage} />
          {staticRoutes.map(({ path, exact = false, wrapperFunc = null }) => (
            <Route
              key={path}
              path={path}
              exact={exact}
              component={wrapperFunc ? wrapperFunc(StaticPage as FunctionComponent<any>) : StaticPage} // eslint-disable-line
            />
          ))}
          <Route exact path="/blog/all" component={BlogListPage} />
          <Route exact path="/blog/:articleSlug" component={BlogPage} />
          <Route exact path="/blog/category/:categorySlug" component={BlogListPage} />
          <Route path="/profile" component={UserIsAuthenticatedOrShowLogin(ProfilePage as FunctionComponent)} />
          <Route path="/beat/:beatId" component={Beat} />
          <Route path="/user/:userId" component={User} />
          <Route path="/settings" component={SettingsPage} />
          <Route exact path="/pay/:type/:paymentId" component={PayPage} />
          <Route exact path="/yoursongs/:userSongId/:location" component={UserIsAuthenticatedOrShowLogin(UserSong as FunctionComponent)} />
          <Route
            exact
            path="/yoursongs/revenuereporting"
            component={UserIsAuthenticatedOrShowLogin(RevenueReportingPage as FunctionComponent)}
          />
          <Route exact path="/yoursongs" component={UserIsAuthenticatedOrShowLogin(UserSongs)} />
          <Route path="/interestform" component={InterestForm} />
          <Route path="/onboard" component={UserIsAuthenticatedOrShowLogin(Onboard as FunctionComponent)} />
          <Route
            path={sidebarRoutes.map(route => route.path)}
            render={() => {
              return (
                <SidebarWithMenuLayout>
                  <SidebarRoutes />
                </SidebarWithMenuLayout>
              )
            }}
          />
          <Route component={ErrorPage} />
        </Switch>
      </main>
      <FixedFooter />
      <Footer />
      {!localStorage.getItem('hide_notifications') && <Notifications />}
    </>
  )
}

export default hot(App)
